import { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useLang } from "@/context/LangContext";

const CACHE_PREFIX = "matpro_btrans_";

function cacheKey(id, target) {
  return `${CACHE_PREFIX}${id}_${target}`;
}

function readCache(id, target) {
  try {
    const v = window.localStorage.getItem(cacheKey(id, target));
    return v ? JSON.parse(v) : null;
  } catch { return null; }
}

function writeCache(id, target, val) {
  try { window.localStorage.setItem(cacheKey(id, target), JSON.stringify(val)); } catch {}
}

/**
 * useBatchTranslate(items)
 *
 * Translates the `title` field of an array of listings in one batch API call.
 * Returns a Map<id, translatedTitle>.
 *
 * - Only fires when lang !== "fr"
 * - Uses localStorage cache per listing id + target lang
 * - Gracefully falls back to original title on any error
 */
export function useBatchTranslate(items) {
  const { lang } = useLang();
  const [titleMap, setTitleMap] = useState({});
  const pendingRef = useRef(false);
  const prevKeyRef = useRef(null);
  const itemsRef = useRef(items);
  itemsRef.current = items;

  const itemsKey = useMemo(() => {
    if (!items?.length) return "";
    return items
      .filter((item) => item?.id && item?.title)
      .map((item) => `${item.id}\x1f${item.title}`)
      .join("\x1e");
  }, [items]);

  useEffect(() => {
    if (!itemsKey || lang === "fr") {
      setTitleMap((prev) => (Object.keys(prev).length === 0 ? prev : {}));
      prevKeyRef.current = null;
      return;
    }

    const batchItems = itemsRef.current?.filter((item) => item?.id && item?.title) || [];

    const toFetch = [];
    const fromCache = {};

    for (const item of batchItems) {
      const cached = readCache(item.id, lang);
      if (cached) {
        fromCache[item.id] = cached;
      } else {
        toFetch.push({ id: item.id, title: item.title });
      }
    }

    const key = `${lang}_${toFetch.map((i) => i.id).join(",")}`;
    if (key === prevKeyRef.current) {
      if (Object.keys(fromCache).length) {
        setTitleMap((prev) => ({ ...prev, ...fromCache }));
      }
      return;
    }
    prevKeyRef.current = key;

    if (Object.keys(fromCache).length) {
      setTitleMap((prev) => ({ ...prev, ...fromCache }));
    }

    if (!toFetch.length) return;
    if (pendingRef.current) return;

    pendingRef.current = true;
    (async () => {
      try {
        const { data } = await api.post("/ai/translate-batch-titles", {
          target: lang,
          items: toFetch,
        });
        const results = data.items || [];
        const next = {};
        for (const r of results) {
          if (r.id && r.title) {
            next[r.id] = r.title;
            writeCache(r.id, lang, r.title);
          }
        }
        setTitleMap((prev) => ({ ...prev, ...next }));
      } catch {
        // Silently fall back — original titles will be shown
      } finally {
        pendingRef.current = false;
      }
    })();
  }, [itemsKey, lang]);

  return titleMap;
}
